import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useUIBridge, type UIBridge } from '@/agent/useUIBridge';

const AgentBridgeContext = createContext<UIBridge | null>(null);

export function useAgentBridge(): UIBridge {
  const ctx = useContext(AgentBridgeContext);
  if (!ctx) {
    throw new Error('useAgentBridge must be used inside <AgentBridgeProvider>');
  }
  return ctx;
}

interface ConfirmRequest {
  prompt: string;
  onConfirmToken: string;
}

export function AgentBridgeProvider({ children }: { children: ReactNode }) {
  const bridge = useUIBridge();
  const bridgeRef = useRef(bridge);
  bridgeRef.current = bridge;

  const [pendingConfirm, setPendingConfirm] = useState<ConfirmRequest | null>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ConfirmRequest>).detail;
      if (detail) setPendingConfirm(detail);
    };
    window.addEventListener('agent:confirm:request', handler);
    return () => window.removeEventListener('agent:confirm:request', handler);
  }, []);

  const handleConfirm = () => {
    if (!pendingConfirm) return;
    window.dispatchEvent(
      new CustomEvent(`agent:confirm:${pendingConfirm.onConfirmToken}`, {
        detail: { confirmed: true },
      }),
    );
    setPendingConfirm(null);
  };

  const handleCancel = () => {
    if (!pendingConfirm) return;
    window.dispatchEvent(
      new CustomEvent(`agent:confirm:${pendingConfirm.onConfirmToken}`, {
        detail: { confirmed: false },
      }),
    );
    setPendingConfirm(null);
  };

  return (
    <AgentBridgeContext.Provider value={bridge}>
      {children}
      <AlertDialog open={pendingConfirm !== null} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmación del agente</AlertDialogTitle>
            <AlertDialogDescription>{pendingConfirm?.prompt}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AgentBridgeContext.Provider>
  );
}
